import React from 'react'
import { Box, Typography } from '@mui/material'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS } from '../../../constants/colors'

export interface GamerFeedbackBackButtonProps {
  /**
   * Button label text (optional - if not provided, only icon is shown)
   */
  label?: string
  /**
   * Click handler
   */
  onClick: () => void
  /**
   * Additional styling
   */
  sx?: any
}

/**
 * GamerFeedbackBackButton - Back navigation button for deeper analysis pages
 *
 * Features:
 * - Arrow icon with optional label
 * - Glassmorphism hover effect
 * - Matches design system styling
 *
 * @example
 * ```tsx
 * <GamerFeedbackBackButton
 *   label="Back to Feedback"
 *   onClick={() => navigate(GamerRoutes.LATEST_SESSION_FEEDBACK)}
 * />
 *
 * // Icon-only version
 * <GamerFeedbackBackButton
 *   onClick={() => navigate(GamerRoutes.MY_PROFILE)}
 * />
 * ```
 */
export const GamerFeedbackBackButton: React.FC<GamerFeedbackBackButtonProps> = ({ label, onClick, sx }) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: label ? '8px' : 0,
        padding: label ? '8px 16px' : '8px',
        cursor: 'pointer',
        borderRadius: '10px',
        transition: 'background 0.2s ease',
        '&:hover': {
          background: INTERAX_COLORS.white10
        },
        ...sx
      }}
    >
      <ArrowLeftIcon
        style={{
          width: '20px',
          height: '20px',
          color: INTERAX_COLORS.sun
        }}
      />
      {label && (
        <Typography
          sx={{
            fontFamily: 'Lexend Deca',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '20px',
            color: INTERAX_COLORS.sun
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  )
}

export default GamerFeedbackBackButton
